import React, { useState, useEffect } from 'react';
import { CircleNotch } from 'phosphor-react';
import { getLearnerRombels, getLearnerRombelStats } from '../../../services/monitorinApi';

const GERMAN_COURSE_ID = "edc49760debb11f08cdde3d082e8673b";

const RombelTable = () => {
  const [rombels, setRombels] = useState([]);
  const [selectedRombelId, setSelectedRombelId] = useState(null);
  const [learnerStats, setLearnerStats] = useState([]);
  const [selectedSubcourseId, setSelectedSubcourseId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState(null);

  // Load rombels on mount
  useEffect(() => {
    loadRombels();
  }, []);

  // Load learner stats when rombel is selected
  useEffect(() => {
    if (selectedRombelId) {
      loadLearnerStats(selectedRombelId);
    } else {
      setLearnerStats([]);
      setSelectedSubcourseId(null);
    }
  }, [selectedRombelId]);

  // Auto-select first subcourse when stats are loaded
  useEffect(() => {
    if (learnerStats.length > 0 && !selectedSubcourseId) {
      const allSubcourses = getAllSubcourses();
      if (allSubcourses.length > 0) {
        setSelectedSubcourseId(allSubcourses[0].id);
      }
    }
  }, [learnerStats]);

  const loadRombels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getLearnerRombels();
      if (response.success) {
        const activeRombels = (response.data || []).filter(r => r['active?']);
        setRombels(activeRombels);
        // Auto-select first rombel if available
        if (activeRombels.length > 0 && !selectedRombelId) {
          setSelectedRombelId(activeRombels[0]._id);
        }
      } else {
        setError(response.message || 'Failed to load rombels');
      }
    } catch (err) {
      setError(err.message || 'Error loading rombels');
    } finally {
      setLoading(false);
    }
  };

  const loadLearnerStats = async (rombelId) => {
    setLoadingStats(true);
    setError(null);
    setSelectedSubcourseId(null);
    try {
      // Get course-id from selected rombel
      const selectedRombel = rombels.find(r => r._id === rombelId);
      if (!selectedRombel || !selectedRombel['course-id']) {
        setError('Course ID not found for selected rombel');
        setLearnerStats([]);
        return;
      }
      const response = await getLearnerRombelStats(selectedRombel['course-id'], rombelId);
      if (response.success) {
        setLearnerStats(response.data || []);
      } else {
        setError(response.message || 'Failed to load learner stats');
        setLearnerStats([]);
      }
    } catch (err) {
      setError(err.message || 'Error loading learner stats');
      setLearnerStats([]);
    } finally {
      setLoadingStats(false);
    }
  };

  // Get all unique subcourses from learner stats
  const getAllSubcourses = () => {
    const subcourseMap = new Map();
    learnerStats.forEach(learner => {
      learner.stats?.forEach(stat => {
        if (stat['subcourse-id'] && stat['subcourse-name']) {
          if (!subcourseMap.has(stat['subcourse-id'])) {
            subcourseMap.set(stat['subcourse-id'], {
              id: stat['subcourse-id'],
              name: stat['subcourse-name'],
            });
          }
        }
      });
    });
    // Sort by name (A1, A2, B1, B2, etc.)
    return Array.from(subcourseMap.values()).sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  };

  const selectedRombel = rombels.find(r => r._id === selectedRombelId);
  const availableSubcourses = getAllSubcourses();

  // Get current logged in user
  const getCurrentUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return userData['universal-learning-id'] || 
             userData.universalLearningId ||
             localStorage.getItem('universal-learning-id');
    } catch {
      return null;
    }
  };

  const currentUserId = getCurrentUserId();

  const formatDuration = (hours) => {
    if (hours === 0) return '0h';
    if (hours < 1) {
      const minutes = Math.round(hours * 60);
      return `${minutes}m`;
    }
    return `${hours.toFixed(1)}h`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };


  const getRowClassName = (rank, isCurrentUser) => {
    const base = 'border-b border-[var(--border-color)] transition-colors duration-200 hover:bg-[var(--table-row-hover)]';
    if (rank === 1) return `${base} bg-amber-500/10 border-l-4 border-l-amber-500 hover:bg-amber-500/15`;
    if (rank === 2) return `${base} bg-gray-400/10 border-l-4 border-l-gray-400 hover:bg-gray-400/15`;
    if (rank === 3) return `${base} bg-amber-800/10 border-l-4 border-l-amber-800 hover:bg-amber-800/15`;
    if (isCurrentUser && rank > 3) return `${base} bg-blue-500/15 border-l-4 border-l-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.2)] hover:bg-blue-500/20`;
    return base;
  };

  return (
    <div className="w-full m-0 p-4 md:p-0 pb-8 md:pb-0">
      <div className="bg-[var(--card-bg)] rounded-2xl p-4 md:p-6 mb-4 shadow-[var(--shadow-sm)] transition-colors duration-300">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
          <div className="flex items-center gap-4">
            <img
              src="/DashboardAsset/throppy.png"
              alt="Trophy"
              className="w-10 h-10 md:w-14 md:h-14 object-contain"
            />
            <h2 className="text-xl md:text-2xl font-bold text-[var(--text-primary)] m-0">Leaderboard</h2>
          </div>
          <select
            className="px-4 py-2.5 border-2 border-[var(--input-border)] rounded-lg text-[15px] font-medium text-[var(--text-primary)] bg-[var(--select-bg)] cursor-pointer transition-all duration-200 min-w-[250px] w-full md:w-auto hover:border-[var(--accent-teal)] focus:outline-none focus:border-[var(--accent-teal)] focus:shadow-[0_0_0_3px_var(--accent-teal-light)] disabled:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-60"
            value={selectedRombelId || ''}
            onChange={(e) => setSelectedRombelId(e.target.value)}
            disabled={loading || rombels.length === 0}
          >
            <option value="">Pilih Rombel</option>
            {rombels.map((rombel) => (
              <option key={rombel._id} value={rombel._id}>
                {rombel.name}
              </option>
            ))}
          </select>
        </div>

        {!loading && selectedRombel && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-3 mt-3 border-t border-[var(--border-color)]">
            <div className="flex flex-wrap items-center gap-8">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">Group Name</span>
                <span className="text-base font-semibold text-[var(--text-primary)]">{selectedRombel.name}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">Start Date:</span>
                <span className="text-base font-semibold text-[var(--text-primary)]">{formatDate(selectedRombel['created-at'])}</span>
              </div>
            </div>
            {!loadingStats && learnerStats.length > 0 && availableSubcourses.length > 0 && (
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 w-full md:w-auto">
                <label htmlFor="subcourse-select" className="text-sm font-semibold text-[var(--text-secondary)]">
                  Pick Subcourse:
                </label>
                <select
                  id="subcourse-select"
                  className="px-4 py-2.5 border-2 border-[var(--input-border)] rounded-lg text-[15px] font-medium text-[var(--text-primary)] bg-[var(--select-bg)] cursor-pointer transition-all duration-200 min-w-[250px] w-full md:w-auto hover:border-[var(--accent-teal)] focus:outline-none focus:border-[var(--accent-teal)] focus:shadow-[0_0_0_3px_var(--accent-teal-light)]"
                  value={selectedSubcourseId || ''}
                  onChange={(e) => setSelectedSubcourseId(e.target.value)}
                >
                  <option value="">Semua Subcourse</option>
                  {availableSubcourses.map((subcourse) => (
                    <option key={subcourse.id} value={subcourse.id}>
                      {subcourse.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300 rounded-lg mb-6 text-sm font-medium">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center min-h-[200px] py-10">
          <div className="flex flex-col items-center justify-center gap-3 p-6 text-[var(--text-secondary)]">
            <CircleNotch size={24} weight="bold" className="animate-spin" />
            <span className="text-[14px] text-[var(--text-secondary)]">Memuat daftar rombel...</span>
          </div>
        </div>
      )}

      {loadingStats && (
        <div className="flex items-center justify-center min-h-[200px] py-10">
          <div className="flex flex-col items-center justify-center gap-3 p-6 text-[var(--text-secondary)]">
            <CircleNotch size={24} weight="bold" className="animate-spin" />
            <span className="text-[14px] text-[var(--text-secondary)]">Memuat statistik learner...</span>
          </div>
        </div>
      )}

      {!loadingStats && learnerStats.length > 0 && (
        <div className="bg-[var(--card-bg)] rounded-2xl shadow-[var(--shadow-sm)] overflow-hidden transition-colors duration-300 overflow-x-auto max-w-full">
          <table className="w-full border-separate border-spacing-0 min-w-[800px]">
            <thead className="bg-gradient-to-r from-[#1d4746] to-[#2d6766] dark:from-[#0f2928] dark:to-[#1a4746]">
              <tr>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider w-20 rounded-tl-2xl">Rank</th>
                <th className="py-4 px-5 text-left text-xs font-semibold text-white uppercase tracking-wider">Learner</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider">Subcourse</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider">OVR</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider">QP</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider">WP</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider">SP</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider">Total Hari</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider">Total Taken</th>
                <th className="py-4 px-5 text-center text-xs font-semibold text-white uppercase tracking-wider rounded-tr-2xl">Total Durasi</th>
              </tr>
            </thead>
            <tbody>
              {learnerStats
                .map((learner, idx) => {
                  // Filter stats by selected subcourse, or show all if none selected
                  let displayStats = learner.stats || [];
                  if (selectedSubcourseId) {
                    displayStats = displayStats.filter(s => s['subcourse-id'] === selectedSubcourseId);
                  }

                  // If filtering by subcourse and no match, skip this learner
                  if (selectedSubcourseId && displayStats.length === 0) {
                    return null;
                  }

                  // Show stats for selected subcourse, or first subcourse if "Semua Subcourse"
                  const targetSubcourse = selectedSubcourseId 
                    ? displayStats[0] 
                    : displayStats[0] || {};
                  const allStats = targetSubcourse.all || {};
                  
                  return {
                    learner,
                    idx,
                    targetSubcourse,
                    allStats,
                  };
                })
                .filter(item => item !== null)
                .sort((a, b) => {
                  // Always sort by highest overall level (descending)
                  const levelA = a.targetSubcourse.all?.level || 0;
                  const levelB = b.targetSubcourse.all?.level || 0;
                  return levelB - levelA;
                })
                .map(({ learner, idx, targetSubcourse, allStats }, rankIndex) => {
                  const overallLevel = targetSubcourse.all?.level || 0;
                  const quickLevel = targetSubcourse.quick?.level || 0;
                  const speakingLevel = targetSubcourse.speaking?.level || 0;
                  const writingLevel = targetSubcourse.writing?.level || 0;
                  const rank = rankIndex + 1;
                  const learnerId = learner['universal-learning-id'];
                  const isCurrentUser = currentUserId && learnerId === currentUserId;
                  
                  return (
                    <tr key={learner['universal-learning-id'] || idx} className={getRowClassName(rank, isCurrentUser)}>
                      <td className="text-center py-3 px-4 align-middle w-20">
                        {rank === 1 && (
                          <img src="/DashboardAsset/9.png" alt="Rank 1" className="w-9 h-9 md:w-12 md:h-12 object-contain inline-block" />
                        )}
                        {rank === 2 && (
                          <img src="/DashboardAsset/10.png" alt="Rank 2" className="w-9 h-9 md:w-12 md:h-12 object-contain inline-block" />
                        )}
                        {rank === 3 && (
                          <img src="/DashboardAsset/11.png" alt="Rank 3" className="w-9 h-9 md:w-12 md:h-12 object-contain inline-block" />
                        )}
                        {rank > 3 && (
                          <span className="text-base md:text-lg font-bold text-[var(--text-secondary)]">{rank}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 align-middle min-w-[150px] md:min-w-[250px]">
                        <div className="text-base md:text-base font-bold text-[var(--text-primary)] mb-1">{learner.name || '-'}</div>
                        <div className="text-sm text-[var(--text-secondary)]">{learner.email || '-'}</div>
                      </td>
                      <td className="text-center py-3 px-4 align-middle">
                        {targetSubcourse['subcourse-name'] || '-'}
                      </td>
                      <td className="text-center py-3 px-4 align-middle text-base md:text-base font-bold text-[var(--text-primary)]">
                        {overallLevel}
                      </td>
                      <td className="text-center py-3 px-4 align-middle text-base md:text-base font-bold text-[var(--text-primary)]">
                        {quickLevel}
                      </td>
                      <td className="text-center py-3 px-4 align-middle text-base md:text-base font-bold text-[var(--text-primary)]">
                        {writingLevel}
                      </td>
                      <td className="text-center py-3 px-4 align-middle text-base md:text-base font-bold text-[var(--text-primary)]">
                        {speakingLevel}
                      </td>
                      <td className="text-center py-3 px-4 align-middle text-[15px] font-semibold text-[var(--text-primary)]">
                        {learner['total-days'] || 0}
                      </td>
                      <td className="text-center py-3 px-4 align-middle text-[15px] font-semibold text-[var(--text-primary)]">
                        {targetSubcourse['total-taken'] || 0}
                      </td>
                      <td className="text-center py-3 px-4 align-middle text-[15px] font-semibold text-[var(--text-primary)]">
                        {selectedSubcourseId 
                          ? formatDuration(targetSubcourse['total-duration'] || 0)
                          : formatDuration(learner['total-duration'] || 0)
                        }
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {!loadingStats && selectedRombelId && learnerStats.length === 0 && !error && (
        <div className="py-12 text-center text-[var(--text-tertiary)] text-[15px] bg-[var(--bg-secondary)] rounded-xl">
          Belum ada data learner untuk rombel ini.
        </div>
      )}
    </div>
  );
};

export default RombelTable;

